import { queriesPayrollRuns } from "@query";
import {
  toWSDateString,
  WSAlert,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import {
  PayrollRunExpand,
  PayrollRunStatus
} from "@wingspanhq/wingspan-api-sdk";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import { Summary } from "../../../../Invoices/components/Summary/Summary";
import { WSQueries } from "../../../../query/WSQuery";
import { FilterableTablePayables } from "../../../Payables/components/TablePayables/FilterableTablePayables";
import { PATH_SETTINGS_PAYROLL_METHOD } from "../../../Settings/paths";
import { PAYROLL_CURRENCY } from "../../constants";
import { selectorPayrollRunContractorAmounts } from "../../selectors/selectorPayrollRunContractorAmounts";
import { selectorPayrollRunDatePaid } from "../../selectors/selectorPayrollRunDatePaid";
import { parsePayrollAmount } from "../../utils";
import { PayrollRunMetadataSidebar } from "../PayrollRunMetadataSidebar";

const EMPTY_VALUE = "–";

type ToolbarBreadcrumbs = NonNullable<
  React.ComponentProps<typeof WSPageToolbar>["breadcrumbs"]
>;
type ToolbarAction = NonNullable<
  React.ComponentProps<typeof WSPageToolbar>["actions"]
>[number];

type Props = {
  payrollRunId: string;
  /** Crumbs of the surface this run was opened from; the run's own is appended. */
  breadcrumbs: ToolbarBreadcrumbs;
  /** Base path the payable detail sidebar mounts under. */
  payablesDetailsBasePath?: string;
};

/**
 * One contractor payroll run, laid out as the retired v1 `PayrollRun` screen
 * was: the run's date as the title, four summary tiles, and the run's payables
 * beneath them.
 *
 * This is not the employee run screen. That one (`PayrollRunDetailsEmployee`)
 * follows the new payroll design — funding card, approval window, wallet
 * balance, work-log and paystub tabs — and the two share nothing but the
 * underlying `getPayrollRun` read.
 *
 * Three v1 tiles map onto v3 imperfectly, each noted at its tile below.
 */
export const PayrollRunDetailsContractor: React.FC<Props> = props => {
  const { payrollRunId } = props;
  const history = useHistory();
  const [isMetadataSidebarVisible, setIsMetadataSidebarVisible] =
    useState(false);

  const queryPayroll = queriesPayrollRuns.getPayrollRun.useQuery({
    payrollRunId,
    expand: [PayrollRunExpand.BREAKDOWN]
  });

  return (
    <WSQueries queries={{ queryPayroll }}>
      {({ queryPayrollData: payrollRun }) => {
        const amounts = selectorPayrollRunContractorAmounts(payrollRun);
        const datePaid = selectorPayrollRunDatePaid(payrollRun);
        const isFailed = payrollRun.status === PayrollRunStatus.FAILED;

        // v1 titled the screen with the run's date and appended "(Overdue)" for
        // a returned debit. v3 has no returned-debit state, so a failed run is
        // the closest thing it can flag (gap G1).
        const title = `${
          datePaid ? toWSDateString(datePaid, "monthDayYear") : "Payroll run"
        }${isFailed ? " (Failed)" : ""}`;

        // Free-form run metadata, behind the v1 screen's "Show details" action.
        // Whether v3 ever populates it is an open question with the API team, so
        // the action only appears when the run actually carries some (gap G6).
        const { metadata } = payrollRun;
        const hasMetadata = !!metadata && Object.keys(metadata).length > 0;

        const toolbarActions: ToolbarAction[] = hasMetadata
          ? [
              {
                label: "Show details",
                buttonKind: "Secondary",
                onClick: () => setIsMetadataSidebarVisible(true)
              }
            ]
          : [];

        return (
          <WSPage>
            <WSPageToolbar
              actions={toolbarActions}
              breadcrumbs={[...props.breadcrumbs, { label: title }]}
              title={title}
            />

            {hasMetadata && isMetadataSidebarVisible && (
              <PayrollRunMetadataSidebar
                metadata={metadata}
                onClose={() => setIsMetadataSidebarVisible(false)}
              />
            )}

            <WSList gap="2XL">
              {isFailed && (
                <WSAlert
                  action={{
                    text: "View payroll funding account settings",
                    onClick: () => history.push(PATH_SETTINGS_PAYROLL_METHOD)
                  }}
                  icon="alert"
                  theme="warning"
                  title="This payroll run was unsuccessful"
                >
                  The payables in this run were not paid out. A common cause is
                  a debit returned by your bank, so check that the payroll
                  funding account has sufficient funds, or update the account
                  you use for payroll funding. Contact us to reprocess the
                  payment once that&apos;s done.
                </WSAlert>
              )}

              <WSGrid>
                <WSGrid.Item span={{ xs: "6", m: "3" }}>
                  <Summary
                    label="Total amount"
                    value={formatMoney(
                      parsePayrollAmount(payrollRun.totalAmount),
                      PAYROLL_CURRENCY
                    )}
                  />
                </WSGrid.Item>
                <WSGrid.Item span={{ xs: "6", m: "3" }}>
                  {/*
                   * v1 counted the run's payables still awaiting approval or
                   * scheduled for payment, by querying them. v3 rolls the same
                   * thing up server-side on the run: opened + pending.
                   */}
                  <Summary
                    label="Pending disbursement"
                    value={
                      amounts.payablesStatuses
                        ? (amounts.payablesStatuses.openedCount ?? 0) +
                          (amounts.payablesStatuses.pendingCount ?? 0)
                        : EMPTY_VALUE
                    }
                  />
                </WSGrid.Item>
                <WSGrid.Item span={{ xs: "6", m: "3" }}>
                  <Summary label="Total 1099s" value={amounts.payablesCount} />
                </WSGrid.Item>
                <WSGrid.Item span={{ xs: "6", m: "3" }}>
                  {/*
                   * v1 showed the estimated deposit date plus one business day.
                   * v3 has no estimated deposit date, only the scheduled pay
                   * date, which does not move when settlement does (gap G4).
                   */}
                  <Summary
                    label="Expected pay date"
                    value={
                      payrollRun.payDate
                        ? toWSDateString(payrollRun.payDate, "monthDayYear")
                        : EMPTY_VALUE
                    }
                  />
                </WSGrid.Item>
              </WSGrid>

              <FilterableTablePayables
                detailsBasePath={props.payablesDetailsBasePath}
                emptyState={{
                  standart: "No payables in this payroll run."
                }}
                payrollRunId={payrollRunId}
                showDownloadCsv
                tab="all"
                visibleColumns={{
                  payee: true,
                  payableNumber: true,
                  amount: true,
                  dueDate: true,
                  openedAt: true,
                  paidAt: true,
                  status: true
                }}
                visibleFilters={{
                  payeeId: true,
                  openedDateRange: true,
                  bulkBatchId: true
                }}
              />
            </WSList>
          </WSPage>
        );
      }}
    </WSQueries>
  );
};
